import * as React from 'react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface ChipOption {
    value: string;
    label: string;
}

export interface ChipGroupProps {
    options: ChipOption[];
    value?: string;
    onChange: (value: string) => void;
    /** Question above the row, e.g. `When would you like to start?`. */
    label: string;
    className?: string;
}

/**
 * A row of pills for a short single choice, where six stacked cards would be
 * more furniture than the answer deserves.
 *
 * Radio semantics for the same reason as `OptionCardGroup`: one choice, one tab
 * stop, arrows to move.
 */
export const ChipGroup = ({
    options,
    value,
    onChange,
    label,
    className,
}: ChipGroupProps) => {
    const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

    const focusOption = (index: number) => {
        const next = (index + options.length) % options.length;
        const option = options[next];
        if (!option) return;
        refs.current[next]?.focus();
        onChange(option.value);
    };

    const activeIndex = Math.max(
        options.findIndex((option) => option.value === value),
        0,
    );

    return (
        <div className={cn('flex flex-col gap-4.5', className)}>
            <p
                className={cn(
                    'font-sans text-4.5 leading-[1.3] tracking-[-0.02em]',
                    QUESTIONNAIRE_COLOUR.ink,
                )}
                id={`chip-group-${label}`}
            >
                {label}
            </p>
            <div
                aria-labelledby={`chip-group-${label}`}
                className="flex flex-wrap gap-2.5"
                role="radiogroup"
            >
                {options.map((option, index) => {
                    const selected = option.value === value;

                    return (
                        <button
                            aria-checked={selected}
                            className={cn(
                                'h-11 rounded-[var(--radius-btn-m)] px-5 font-sans text-4.5 leading-[1.3] tracking-[-0.01em] transition-colors',
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                                selected
                                    ? 'bg-[#181818] text-white'
                                    : cn(
                                          'border border-[#eaeaea] bg-white hover:bg-[#f6f6f6]',
                                          QUESTIONNAIRE_COLOUR.ink,
                                      ),
                            )}
                            key={option.value}
                            onClick={() => onChange(option.value)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'ArrowRight' ||
                                    event.key === 'ArrowDown'
                                ) {
                                    event.preventDefault();
                                    focusOption(index + 1);
                                }
                                if (
                                    event.key === 'ArrowLeft' ||
                                    event.key === 'ArrowUp'
                                ) {
                                    event.preventDefault();
                                    focusOption(index - 1);
                                }
                            }}
                            ref={(node) => {
                                refs.current[index] = node;
                            }}
                            role="radio"
                            tabIndex={index === activeIndex ? 0 : -1}
                            type="button"
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
